<template>
  <Modal
    :show="modalVisible"
    action-text="Apply Template"
    :hide-default-actions="showNoTemplatesNote"
    :disabled="!template"
    width="60%"
    height="80%"
    content-class="job-apply-template-content"
    @action="submitForm"
    @close="close"
  >
    <template
      v-if="showNoTemplatesNote"
      #title
    >
      <div class="d-flex">
        <div class="alert-icon">
          <AlertIcon
            type="warning"
          />
        </div>

        No templates available
      </div>
    </template>

    <template
      v-else
      #title
    >
      Apply a Template
    </template>

    <template
      v-if="showNoTemplatesNote"
      #content
    >
      <div>
        {{ companyName || 'company' }}'s Datapeople administrators have not set up any templates yet.
      </div>
      <div class="mt-4">
        <router-link :to="{ name: 'administrators' }">
          Read more here <span class="text-arrow ml-1">🡢</span>
        </router-link>
      </div>
    </template>

    <template
      v-else
      #content
    >
      <div class="d-flex ga-6 h-100 pb-4">
        <DpplHeaderFooterContent class="w-50">
          <template #header>
            <div class="mb-4">
              Use a template to save time, stay consistent, and
              align with {{ companyName || 'company' }}'s standards.
            </div>
          </template>

          <div class="template-list w-100">
            <v-sheet
              v-for="item in templatesWithDefaultLabeled"
              :key="item.id"
              class="template-list-item pa-3 rounded-lg border-sm cursor-pointer"
              :class="{ selected: template === item.id }"
              @click="selectTemplate(item)"
            >
              {{ item.title }}
            </v-sheet>
          </div>

          <template #footer>
            <div
              v-if="showNoContentNote"
              class="mt-4"
            >
              <span class="font-weight-bold">Note:</span>
              Since this job is currently empty, applying a template will overwrite it with
              the template content.
            </div>
          </template>
        </DpplHeaderFooterContent>

        <v-divider vertical />

        <div class="w-50 h-100 overflow-y-auto">
          <TemplatePreviewPanel :template-id="template" />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { recruitJsonApplyTemplate } from '@/api/recruitJson';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useApplyTemplate } from '@/stores/document/applyTemplate';
import { useDatapeopleAnywhereEditor } from '@/stores/document/datapeopleAnywhereEditor';
import { useColors } from '@/composables/colors';
import { useEditor } from '@/stores/editor/editor';
import Modal from '@/components/shared/Modal.vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppLoading } from '@/stores/app/loading';
import AlertIcon from '@/components/shared/alerts/AlertIcon.vue';
import TemplatePreviewPanel from '@/components/shared/TemplatePreviewPanel.vue';
import DpplHeaderFooterContent from '@ui/components/general/DpplHeaderFooterContent/DpplHeaderFooterContent.vue';

export default {
  name: 'ApplyTemplateModal',

  components: {
    AlertIcon,
    DpplHeaderFooterContent,
    Modal,
    TemplatePreviewPanel,
  },

  props: {
    revisionId: { type: Number, default: null },
    jobId: { type: [String, Number], default: null },
  },

  emits: ['tracking-event'],

  setup() {
    const { basicRed } = useColors();
    return { basicRed };
  },

  data() {
    return {
      template: null,
    };
  },

  computed: {
    ...mapState(useAppLoading, ['setLoading', 'modalLoading']),
    ...mapState(useApplyTemplate, ['modalVisible']),
    ...mapState(useEditor, ['hasContent', 'templateList']),
    ...mapState(useDatapeopleAnywhereEditor, ['isDatapeopleAnywhereEditor']),

    templatesWithDefaultLabeled() {
      const templateCopy = this.templateList.map((template) => ({ ...template }));
      const defaultIndex = templateCopy.findIndex((template) => template.is_default === true);
      if (defaultIndex !== -1) {
        templateCopy[defaultIndex].title = `${templateCopy[defaultIndex].title} (Default)`;
      }
      return templateCopy;
    },

    showNoContentNote() {
      return !this.showNoTemplatesNote && !this.hasContent;
    },

    showNoTemplatesNote() {
      return this.templateList.length < 1;
    },

    companyName() {
      return useAppAuthData().authData.companyName;
    },
  },

  mounted() {
    const defaultTemplate = this.templatesWithDefaultLabeled
      .find((template) => template.is_default === true);
    const firstTemplate = this.templateList.length ? this.templateList[0] : false;

    if (defaultTemplate) {
      this.template = defaultTemplate.id;
    } else if (!defaultTemplate && firstTemplate) {
      this.template = firstTemplate.id;
    }
  },

  methods: {
    ...mapActions(useAppSnackbar, ['addErrorSnackbar']),
    ...mapActions(useDatapeopleAnywhereEditor, ['applyTemplateToDatapeoleAnywhereJob']),

    selectTemplate(item) {
      if (this.template === item.id) {
        this.template = null;
      } else {
        this.template = item.id;
        this.$emit('tracking-event', { action: 'expandedTemplateOptions' });
      }
    },

    async submitForm() {
      if (!this.template) return;

      try {
        this.$emit('tracking-event', {
          action: 'applyTemplateModalClick',
          args: { id: this.jobId, revisionId: this.revisionId },
        });

        this.setLoading({ modalLoading: true });

        if (this.isDatapeopleAnywhereEditor) {
          await this.applyTemplateToDatapeoleAnywhereJob({
            template: this.template, hasContent: this.hasContent,
          });
        } else {
          const { newRevisionId } = await recruitJsonApplyTemplate({
            jobId: this.jobId,
            templateId: this.template,
            actionType: 'replace',
          });

          this.$emit('tracking-event', {
            action: 'templateApplied',
            args: {
              id: this.jobId,
              revisionId: this.revisionId,
              newRevisionId,
            },
          });

          await this.$router.replace({
            name: 'editor-view-reload',
            params: { id: this.jobId },
            query: {
              ...this.$route.query,
              ...(
                this.hasContent
                  ? { sideRevision: this.revisionId }
                  : {}
              ),
            },
          });
        }
      } catch (e) {
        this.addErrorSnackbar({ text: `Apply template error: ${e.message}`, timeout: 5000 });
        throw e;
      } finally {
        this.setLoading({ modalLoading: false });
        this.close();
      }
    },
    close() {
      useApplyTemplate().setModalVisible(false);
    },
  },
};
</script>

<style scoped lang="less">
.template-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
}
.template-list-item {
  user-select: none;

  &:hover {
    border-color: @brand-primary;
    background-color: fade(@brand-primary, 5%);
  }

  &.selected {
    border-color: @brand-primary;
    background-color: fade(@brand-primary, 10%);
    color: @brand-primary;
  }
}
.alert-icon {
  :deep(.alert-icon-wrapper) {
    color: @basic-red;
    width: 24px;
    height: 24px;
    border: 1px solid @basic-red;
  }
}
.text-arrow {
  font-size: 17px;
}
</style>

<style lang="less">
.job-apply-template-content {
  & > .v-sheet {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 80vh;
  }
  .modal-content-wrapper {
    flex-grow: 1;
    min-height: 0;
    overflow: hidden;
    .content {
      overflow: hidden;
      height: 100%;
    }
  }
  .actions {
    width: 50%;
  }
}
</style>
